import {InCareNetHFFormValues} from "../components/forms/productForms/InCareNetHFProductForm";
import {InCareNetHFTableData} from "../models/InCareNetHF";
import dayjs from "dayjs";
import {addSoftware, deleteSoftware, getSoftware, getSoftwareAtDate, updateSoftware} from "../api/software";
import {sumArray} from "../utils/helperFunctions";
import {activeFieldNames, inActiveFieldNames} from "../constants";
import {ClientICD, DbCustomer, LeanDeviceCounts} from "sharedlibs";

export default class InCareNetHFService {

  static deviceCountColumns = [
    "activeAbbott",
    "activeBio",
    "activeBoston",
    "activeMedtronic",
    "activeMicroPort",
    "externalSensors",
    "totalActiveImplants",
    "inactiveAbbott",
    "inactiveBio",
    "inactiveBoston",
    "inactiveMedtronic",
    "inactiveMicroPort",
    "totalInactiveImplants",
  ]

  static deviceCountManufacturers = [
    "Abbott",
    "Bio",
    "Boston",
    "Medtronic",
    "MicroPort"
  ]

  static async getInCareNetHFs(query?: any): Promise<ClientICD[]> {
    const response = await getSoftware(query)

    if (!Array.isArray(response)) {
      throw new Error("Error while fetching inCareNet HF objects")
    }

    return response
  }

  static async addInCareNetHF(customer: DbCustomer, formObject: InCareNetHFFormValues) {
    const icd = this.createNewIcn(customer, formObject)

    if (!(await this.checkUniqueProperties(icd))) {
      alert("URL of inCareNet is not unique!")
      throw new Error("InCareNet values are not unique!")
    }

    console.log('Validation passed. Adding inCareNet HF')
    await addSoftware(icd)
  }

  static async updateInCareNetHF(oldIcd: ClientICD, formObject: InCareNetHFFormValues) {
    if (!oldIcd.id) {
      throw new Error('Validation Error, it appears the given InCareNetHF object is missing an id property')
    }

    const updatedIcd = this.updateIcnObjectValues(oldIcd, formObject)

    if (!(await this.checkUniqueProperties(updatedIcd))) {
      alert("URL of inCareNet is not unique!")
      throw new Error("InCareNet values are not unique!")
    }

    console.log('Validation passed. Updating inCareNet HF...')
    await updateSoftware(updatedIcd)
  }

  static async deleteInCareNetHF(icnId: string) {
    await deleteSoftware(icnId)
  }

  private static updateIcnObjectValues(icn: ClientICD, formObject: InCareNetHFFormValues): ClientICD {
    return {
      ...icn,
      url: formObject.url,
      token: formObject.token,
      os: formObject.os,
      priceSensorsPerPatient: parseFloat(formObject.priceSensorsPerPatient),
      priceImplantsPerPatient: parseFloat(formObject.priceImplantsPerPatient),
      getemedHotlineSupport: formObject.getemedHotlineSupport,
      tmzActiveSince: formObject.tmzActiveSince ? formObject.tmzActiveSince.format('YYYY-MM-DD') : "",
      system: formObject.system,
      disabled: formObject.disabled,
      contractTerminated: formObject.contractTerminated,
      plannedDeactivationDate: formObject.plannedDeactivationDate
        ? formObject.plannedDeactivationDate.format('YYYY-MM-DD')
        : null,
      pauseUntil: formObject.pauseUntil
        ? formObject.pauseUntil.format('YYYY-MM-DD')
        : null,
      nonGBASensorPrice: formObject.nonGBASensorPrice,
      nonGBAImplantPrice: formObject.nonGBAImplantPrice,
      salesPersonId: formObject.salesPersonId,
      implantCorrection: formObject.implantCorrection,
      extSensorsCorrection: formObject.extSensorsCorrection,
    }
  }

  private static createNewIcn(customer: DbCustomer, formObject: InCareNetHFFormValues): ClientICD {
    if (customer.id === undefined) {
      throw new Error("CustomerId cannot be undefined.")
    }

    return {
      abbottId: customer.abbottId,
      biotronikClientName: customer.biotronikClientName,
      bostonSAPID: customer.bostonSAPID,
      currentDeviceCount: this.EMPTY_DEVICE_COUNTS, // Will be updated by backend
      customerId: customer.id,
      customerName: customer.name,
      customerNumber: customer.customerNumber,
      implantCorrection: formObject.implantCorrection,
      extSensorsCorrection: formObject.extSensorsCorrection,
      getemedHotlineSupport: formObject.getemedHotlineSupport,
      city: customer.address.city,
      priceImplantsPerPatient: parseFloat(formObject.priceImplantsPerPatient),
      priceSensorsPerPatient: parseFloat(formObject.priceSensorsPerPatient),
      solName: "incardio-dashboard",
      tmzActiveSince: formObject.tmzActiveSince ? formObject.tmzActiveSince.format('YYYY-MM-DD') : "",
      token: formObject.token,
      url: formObject.url,
      version: "",
      os: formObject.os,
      system: formObject.system,
      nonGBASensorPrice: formObject.nonGBASensorPrice,
      nonGBAImplantPrice: formObject.nonGBAImplantPrice,
      disabled: formObject.disabled,
      contractTerminated: formObject.contractTerminated,
      plannedDeactivationDate: formObject.plannedDeactivationDate
        ? formObject.plannedDeactivationDate.format('YYYY-MM-DD')
        : null,
      salesPersonId: formObject.salesPersonId
    }
  }

  private static readonly EMPTY_DEVICE_COUNTS = {
    deviceCountActive: {
      Bio: 0,
      Abbott: 0,
      Boston: 0,
      Medtronic: 0,
      MicroPort: 0,
      ExternalSensors: 0,
    },
    deviceCountInactive: {
      Bio: -1,
      Abbott: -1,
      Boston: -1,
      Medtronic: -1,
      MicroPort: -1,
    }
  }


  static transformToClientICD(data: InCareNetHFTableData): ClientICD {
    const currentDeviceCount: LeanDeviceCounts = {
      deviceCountActive: {
        Bio: data.activeBio,
        Abbott: data.activeAbbott,
        Boston: data.activeBoston,
        Medtronic: data.activeMedtronic,
        MicroPort: data.activeMicroPort,
        ExternalSensors: data.externalSensors
      },
      deviceCountInactive: {
        Bio: data.inactiveBio,
        Abbott: data.inactiveAbbott,
        Boston: data.inactiveBoston,
        Medtronic: data.inactiveMedtronic,
        MicroPort: data.inactiveMicroPort
      }
    };

    const icd: ClientICD = {
      id: data.id,
      version: data.version,
      solName: "incardio-dashboard",
      customerId: data.customerId,
      customerName: data.customerName,
      customerNumber: data.customerNumber,
      url: data.url,
      system: data.system,
      os: data.os,
      token: "", // default or fetched elsewhere
      getemedHotlineSupport: typeof data.getemedHotlineSupport === "boolean"
        ? data.getemedHotlineSupport
        : true, // fallback if it's an HTML Element

      priceImplantsPerPatient: data.priceImplantsPerPatient,
      priceSensorsPerPatient: data.priceSensorsPerPatient,
      tmzActiveSince: data.tmzActiveSince,

      city: data.location,

      currentDeviceCount,

      // Default or placeholder values
      biotronikClientName: "",
      bostonSAPID: "",
      abbottId: "",
      implantCorrection: data.implantCorrection,
      extSensorsCorrection: data.extSensorsCorrection,
      disabled: data.disabled,
      contractTerminated: data.contractTerminated,
      contractTerminatedAt: data.contractTerminatedAt ?? null,
      plannedDeactivationDate: data.plannedDeactivationDate ?? null,
      pauseUntil: data.pauseUntil ?? null,
      nonGBASensorPrice: data.nonGBASensorPrice,
      nonGBAImplantPrice: data.nonGBAImplantPrice,
      salesPersonId: data.salesPersonId
    };

    return icd;
  }

  static mapInCareNetHFObjectToFormObject(icnObject: ClientICD): InCareNetHFFormValues {
    return {
      id: icnObject.id ?? "",
      getemedHotlineSupport: icnObject.getemedHotlineSupport,
      os: icnObject.os ?? "",
      priceImplantsPerPatient: icnObject.priceImplantsPerPatient.toString(),
      priceSensorsPerPatient: icnObject.priceSensorsPerPatient.toString(),
      tmzActiveSince: dayjs(icnObject.tmzActiveSince),
      token: icnObject.token,
      url: icnObject.url,
      system: icnObject.system,
      nonGBASensorPrice: icnObject.nonGBASensorPrice,
      nonGBAImplantPrice: icnObject.nonGBAImplantPrice,
      disabled: icnObject.disabled,
      contractTerminated: icnObject.contractTerminated,
      contractTerminatedAt: icnObject.contractTerminatedAt ?? null,
      plannedDeactivationDate: icnObject.plannedDeactivationDate
        ? dayjs(icnObject.plannedDeactivationDate)
        : null,
      disabledAt: icnObject.disabledAt
        ? dayjs(icnObject.disabledAt)
        : null,
      pauseUntil: icnObject.pauseUntil
        ? dayjs(icnObject.pauseUntil)
        : null,
      implantCorrection: icnObject.implantCorrection,
      extSensorsCorrection: icnObject.extSensorsCorrection,
      salesPersonId: icnObject.salesPersonId,
      medtronicUsername: icnObject.medtronicUsername ?? ""
    }
  }

  static async getTableInCareNetHFData(date?: string): Promise<InCareNetHFTableData[]> {
    let inCareNetHFs;
    if (date) {
      // Use the at-date endpoint with the specified date
      inCareNetHFs = await getSoftwareAtDate({date});
    } else {
      // Use the default endpoint
      inCareNetHFs = await getSoftware();
    }
    return inCareNetHFs.map((elem) => {
      if (elem.id === undefined) {
        throw new Error(`Found not identifiable ICN object: ${elem}`)
      }

      const activeCounts = [
        elem.currentDeviceCount.deviceCountActive.Abbott,
        elem.currentDeviceCount.deviceCountActive.Bio,
        elem.currentDeviceCount.deviceCountActive.Boston,
        elem.currentDeviceCount.deviceCountActive.Medtronic,
        elem.currentDeviceCount.deviceCountActive.MicroPort
      ]
      const totalActiveImplants = sumArray(activeCounts.filter(v => v >= 0))

      const inactiveCounts = [
        elem.currentDeviceCount.deviceCountInactive.Abbott,
        elem.currentDeviceCount.deviceCountInactive.Bio,
        elem.currentDeviceCount.deviceCountInactive.Boston,
        elem.currentDeviceCount.deviceCountInactive.Medtronic,
        elem.currentDeviceCount.deviceCountInactive.MicroPort
      ]
      const hasInactiveError = inactiveCounts.some(v => v < 0)
      const totalInactiveImplants = sumArray(inactiveCounts.filter(v => v >= 0))

      return {
        id: elem.id,
        customerId: elem.customerId, // Necessary to get customer related data
        activeAbbott: elem.currentDeviceCount.deviceCountActive.Abbott,
        activeBio: elem.currentDeviceCount.deviceCountActive.Bio,
        activeBoston: elem.currentDeviceCount.deviceCountActive.Boston,
        activeMedtronic: elem.currentDeviceCount.deviceCountActive.Medtronic,
        activeMicroPort: elem.currentDeviceCount.deviceCountActive.MicroPort,
        customerName: elem.customerName,
        customerNumber: elem.customerNumber,
        externalSensors: elem.currentDeviceCount.deviceCountActive.ExternalSensors,
        getemedHotlineSupport: elem.getemedHotlineSupport,
        inactiveAbbott: elem.currentDeviceCount.deviceCountInactive.Abbott,
        inactiveBio: elem.currentDeviceCount.deviceCountInactive.Bio,
        inactiveBoston: elem.currentDeviceCount.deviceCountInactive.Boston,
        inactiveMedtronic: elem.currentDeviceCount.deviceCountInactive.Medtronic,
        inactiveMicroPort: elem.currentDeviceCount.deviceCountInactive.MicroPort,
        location: elem.city,
        totalActiveImplants,
        totalInactiveImplants,
        implantConnectionRate: getImplantConnectionRate(totalActiveImplants, totalInactiveImplants, hasInactiveError),
        version: elem.version,
        url: elem.url,
        tmzActiveSince: elem.tmzActiveSince,
        priceImplantsPerPatient: elem.priceImplantsPerPatient,
        priceSensorsPerPatient: elem.priceSensorsPerPatient,
        os: elem.os ?? "",
        system: elem.system,
        disabled: elem.disabled,
        contractTerminated: elem.contractTerminated,
        contractTerminatedAt: elem.contractTerminatedAt ?? null,
        plannedDeactivationDate: elem.plannedDeactivationDate ?? null,
        pauseUntil: elem.pauseUntil ?? null,
        nonGBASensorPrice: elem.nonGBASensorPrice,
        nonGBAImplantPrice: elem.nonGBAImplantPrice,
        implantCorrection: elem.implantCorrection,
        extSensorsCorrection: elem.extSensorsCorrection,
        salesPersonId: elem.salesPersonId,
        lastRefreshTimestamp: elem.lastRefreshTimestamp ?? null,
        lastRefreshSuccess: elem.lastRefreshSuccess ?? null,
      }
    })

    /**
     * Calculates: implantConnectionRate = totalActive / (totalActive + totalInactive)
     * Returns NaN if totalInactive < 0, which indicates device count fetch error.
     * @param totalActive Total amount of actively monitored implants
     * @param totalInactive Total amount of not actively monitored implants remaining in the inSuite
     */
    function getImplantConnectionRate(totalActive: number, totalInactive: number, hasInactiveError: boolean): number {
      if (hasInactiveError) { // If any inactive device count fetch failed
        return NaN
      }

      if ((totalActive + totalInactive) === 0) {
        return 0
      }

      const rate = totalActive / (totalActive + totalInactive)
      return Math.round(parseFloat(rate.toFixed(2)) * 100) // calculate percentage
    }
  }

  static getSumOfDeviceCount(sol: ClientICD, isActive: boolean) {
    let sum = 0;

    if (sol.currentDeviceCount) {
      const currentDeviceCount = isActive ? sol.currentDeviceCount.deviceCountActive
        : sol.currentDeviceCount.deviceCountInactive

      for (let [key, value] of Object.entries(currentDeviceCount)) {
        if (key !== 'ExternalSensors') {
          sum += value;
        }
      }

    }

    return sum
  }

  static aggregateDeviceCount(tableData: InCareNetHFTableData[], column: {field: string}): number | null {
    let sum = 0;

    for (const elem of tableData) {
      const value = elem[column.field]

      if (value === undefined) {
        continue
      }

      if (
        typeof value !== 'number' ||
        !(activeFieldNames.includes(column.field) || inActiveFieldNames.includes(column.field))
      ) {
        console.error(`Could not aggregate device counts from ${column.field} with value: ${value?.toString()}`)
        return null
      }

      if (value < 0) {
        continue // Do not add negative values, those indicate error with device count fetch
      }
      sum += value

    }
    return sum;
  }

  static isDeviceCountValid(value: any): { error: boolean, message?: string } {
    if (typeof value === 'number' && value >= 0) {
      return {error: false}
    }
    return {error: true, message: `Device count fetch error: Check URL and Token.`}
  }

  /**
   * Validates the url necessary for ICN device count fetch.
   * Returns "true" if pattern matches, an error string else.
   * @param str
   */
  static validateICNUrl(str: string): boolean | string {
    const urlPattern = new RegExp(
      '^https:\\/\\/' + // mandatory https protocol
      '([a-zA-Z\\d-]+\\.)+[a-zA-Z]{2,}' + // domain name with mandatory TLD
      '(\\:\\d+)?' + // optional port
      '$' // end of string, no path or query allowed
    );

    return urlPattern.test(str) || "Ungültige URL";
  }


  /**
   * This function checks properties which must be unique in the InCareNetHF.
   * At the moment, this is only the "url" property.
   * Returns true if values are unique, false otherwise.
   * @param icn
   */
  static async checkUniqueProperties(icn: ClientICD): Promise<boolean> {
    const result = await getSoftware()
    const sameUrl = result.find((elem: any) => {
      return elem.url === icn.url && elem.id !== icn.id
    })
    return !sameUrl
  }
}
